import { gql } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import {
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Separator,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import { usePullToRefresh } from '@web/src/contexts/pull-to-refresh-context'
import EventAttendeeDialog, {
  UPDATE_EVENT_ATTENDEE,
} from '@web/src/features/event/event-attendee-dialog/event-attendee-dialog'
import EventAttendeesHeader from '@web/src/features/event/event-attendees-header/event-attendees-header'
import { getEventAttendanceStatusBadge, getEventRoleBadge } from '@web/src/features/event/utils/eventBadges'
import {
  type PointerEdgeAutoScrollSession,
  startPointerEdgeAutoScroll,
} from '@web/src/hooks/use-pointer-edge-auto-scroll'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  CircleDollarSignIcon,
  ClipboardListIcon,
  EllipsisIcon,
  GripVerticalIcon,
  PencilIcon,
  ShieldQuestionIcon,
  UserIcon,
  UserMinusIcon,
  UserSearchIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'
import { Reorder, useDragControls } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EventAttendanceFilterStatus,
  EventAttendeeSortEnum,
  EventAttendeesListPageQuery,
  EventAttendeesListPageQueryVariables,
  EventAttendeesPageQuery,
  EventRole,
} from 'types/graphql'
import { z } from 'zod'
import { EVENT_ATTENDEES_QUERY, EVENT_QUERY, REMOVE_EVENT_ATTENDEE } from './$eventIdOrSlug_.attendees'

dayjs.extend(relativeTime)

type WaitlistAttendee = NonNullable<EventAttendeesListPageQuery['eventAttendeeListPage']>['entries'][number]

function reorderWaitlistEntries(entries: WaitlistAttendee[], id: string, toIndex: number) {
  const fromIndex = entries.findIndex((entry) => entry.id === id)
  if (fromIndex === -1) {
    return entries
  }

  const next = [...entries]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

/** 1-based waitlist rank for a row on the current page (accounts for pagination). */
function waitlistRankOnPage(localIndex: number, page: number, perPage: number) {
  return localIndex + 1 + (page - 1) * perPage
}

const ATTENDEE_STATUS_BY_ACTION: Record<string, 'GOING' | 'MAYBE' | 'NOTGOING' | 'WAITLISTED'> = {
  going: 'GOING',
  maybe: 'MAYBE',
  notgoing: 'NOTGOING',
  waitlisted: 'WAITLISTED',
}

const SET_WAITLIST_POSITION = gql`
  mutation SetWaitlistPosition($id: String!, $position: Int!) {
    setWaitlistPosition(id: $id, position: $position) {
      id
      waitlistPriority
    }
  }
`

type WaitlistSearchInput = {
  page?: number
  name?: string
  role?: string
}

type WaitlistLoaderDeps = {
  page: number
  name?: string
  role?: string
}

function resolveWaitlistSearchParams(search: WaitlistSearchInput): WaitlistLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    role: search.role,
  }
}

function waitlistAttendeesQueryVariables(
  eventIdOrSlug: string,
  deps: WaitlistLoaderDeps
): EventAttendeesListPageQueryVariables {
  return {
    page: deps.page,
    filter: {
      eventId: eventIdOrSlug,
      name: deps.name,
      role: deps.role as EventRole | undefined,
      statuses: ['WAITLISTED'] as EventAttendanceFilterStatus[],
    },
    sortBy: 'WAITLIST_PRIORITY' as EventAttendeeSortEnum,
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/event/$eventIdOrSlug_/waitlist')({
  component: EventAttendeesWaitlistPage,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    role: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        role: '',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Event Waitlist | ${env.VITE_APP_NAME}`,
      description: 'Manage event waitlist and attendee priorities.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loaderDeps: ({ search }) => resolveWaitlistSearchParams(search),
  loader: async ({ context, params, deps }) => {
    const resolvedDeps = deps as WaitlistLoaderDeps
    const variables = waitlistAttendeesQueryVariables(params.eventIdOrSlug, resolvedDeps)

    const [{ queryRef: eventQueryRef }, { queryRef: attendeesQueryRef }] = await Promise.all([
      queryAndPreload(context, EVENT_QUERY, {
        variables: { eventIdOrSlug: params.eventIdOrSlug },
      }),
      queryAndPreload(context, EVENT_ATTENDEES_QUERY, { variables }),
    ])

    return { eventQueryRef, attendeesQueryRef }
  },
})

function EventAttendeesWaitlistPage() {
  const { t } = useTranslation()
  const { eventIdOrSlug } = Route.useParams()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveWaitlistSearchParams(search), [search])
  const navigate = useNavigate()
  const { cache } = useApolloClient()
  const [removeModal, setRemoveModal] = useState(false)
  const [attendeeToRemove, setAttendeeToRemove] = useState<WaitlistAttendee | null>(null)
  const [selectedAttendee, setSelectedAttendee] = useState<WaitlistAttendee | null>(null)
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)

  const { eventQueryRef, attendeesQueryRef } = Route.useLoaderData()
  const { data: eventData, error: eventError } = useReadQuery(eventQueryRef as QueryRef<EventAttendeesPageQuery>)
  const { data, error } = useReadQuery(attendeesQueryRef as QueryRef<EventAttendeesListPageQuery>)
  const [isLoading, setIsLoading] = useState(false)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  // Prevent hydration mismatch by only setting loading state on client
  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const attendeesQueryVariables = useMemo(
    () => waitlistAttendeesQueryVariables(eventIdOrSlug, resolvedSearch),
    [eventIdOrSlug, resolvedSearch]
  )

  const formDefaultValues = useMemo(() => ({ name: resolvedSearch.name ?? '' }), [resolvedSearch.name])

  const formMethods = useForm({
    defaultValues: formDefaultValues,
  })

  const pageData = useMemo(
    () => data?.eventAttendeeListPage || { entries: [], count: 0, perPage: 24 },
    [data?.eventAttendeeListPage]
  )

  const currentPage = resolvedSearch.page

  /** Local order during drag/reorder; null means use server `pageData.entries` (SSR-safe). */
  const [orderedEntriesOverride, setOrderedEntriesOverride] = useState<WaitlistAttendee[] | null>(null)
  const displayEntries = orderedEntriesOverride ?? pageData.entries
  const orderedEntriesRef = useRef(displayEntries)
  const isDraggingRef = useRef(false)
  const draggedAttendeeIdRef = useRef<string | null>(null)
  const orderBeforeDragRef = useRef<string[]>([])
  const dragScrollSessionRef = useRef<PointerEdgeAutoScrollSession | null>(null)
  const [clientMounted, setClientMounted] = useState(false)
  const { setDisabled: setPullToRefreshDisabled } = usePullToRefresh()

  orderedEntriesRef.current = displayEntries

  useEffect(() => {
    setClientMounted(true)
  }, [])

  useEffect(() => {
    return () => {
      dragScrollSessionRef.current?.stop()
    }
  }, [])

  const [updateEventAttendee] = useMutation(UPDATE_EVENT_ATTENDEE, {
    onCompleted: () => {
      toast.success(t('Attendee updated successfully'))
      navigate({ search: { ...search } } as never)
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  const [removeEventAttendee] = useMutation(REMOVE_EVENT_ATTENDEE, {
    onCompleted: () => {
      toast.success(t('Attendee removed successfully'))
      setRemoveModal(false)
      navigate({ search: { ...search } } as never)
    },
    onError: (err) => {
      toast.error(err.message)
      setRemoveModal(false)
    },
  })

  const syncWaitlistCache = useCallback(
    (entries: WaitlistAttendee[], movedAttendee?: { id: string; waitlistPriority: number }) => {
      cache.updateQuery<EventAttendeesListPageQuery, EventAttendeesListPageQueryVariables>(
        {
          query: EVENT_ATTENDEES_QUERY,
          variables: attendeesQueryVariables,
        },
        (existing) => {
          if (!existing?.eventAttendeeListPage) {
            return existing
          }

          const updatedEntries = entries.map((entry) =>
            entry.id === movedAttendee?.id ? { ...entry, waitlistPriority: movedAttendee.waitlistPriority } : entry
          )

          return {
            ...existing,
            eventAttendeeListPage: {
              ...existing.eventAttendeeListPage,
              entries: updatedEntries,
            },
          }
        }
      )
    },
    [attendeesQueryVariables, cache]
  )

  const [setWaitlistPosition] = useMutation(SET_WAITLIST_POSITION, {
    onCompleted: (mutationData: { setWaitlistPosition: { id: string; waitlistPriority: number } }) => {
      toast.success(t('Waitlist priority updated successfully'))
      syncWaitlistCache(orderedEntriesRef.current, mutationData.setWaitlistPosition)
    },
    onError: (err) => {
      toast.error(err.message)
      const cached = cache.readQuery<EventAttendeesListPageQuery, EventAttendeesListPageQueryVariables>({
        query: EVENT_ATTENDEES_QUERY,
        variables: attendeesQueryVariables,
      })
      if (cached?.eventAttendeeListPage?.entries) {
        setOrderedEntriesOverride(cached.eventAttendeeListPage.entries)
      }
    },
  })

  const handlePageChange = useCallback(
    (nextPage: { page: number }) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          page: 1,
          name: value || undefined,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const applyWaitlistMove = useCallback(
    async (attendeeId: string, position: number, optimisticEntries: WaitlistAttendee[]) => {
      setOrderedEntriesOverride(optimisticEntries)
      await setWaitlistPosition({ variables: { id: attendeeId, position } })
    },
    [setWaitlistPosition]
  )

  const handleWaitlistMoveAction = useCallback(
    async (attendee: WaitlistAttendee, action: 'move-to-top' | 'move-to-bottom') => {
      const entries = orderedEntriesRef.current
      const { count: waitlistCount, perPage } = pageData
      const lastPage = Math.max(1, Math.ceil(waitlistCount / perPage))

      if (action === 'move-to-top') {
        const optimistic =
          currentPage === 1
            ? reorderWaitlistEntries(entries, attendee.id, 0)
            : entries.filter((entry) => entry.id !== attendee.id)
        await applyWaitlistMove(attendee.id, 1, optimistic)
        return
      }

      const optimistic =
        currentPage === lastPage
          ? reorderWaitlistEntries(entries, attendee.id, entries.length - 1)
          : entries.filter((entry) => entry.id !== attendee.id)
      await applyWaitlistMove(attendee.id, waitlistCount, optimistic)
    },
    [applyWaitlistMove, currentPage, pageData]
  )

  const handleAction = useCallback(
    (attendee: WaitlistAttendee, action: string) => {
      if (action === 'remove') {
        setAttendeeToRemove(attendee)
        setRemoveModal(true)
        return
      }
      if (action === 'EDIT') {
        setSelectedAttendee(attendee)
        setPaymentDialogOpen(true)
        return
      }

      const status = ATTENDEE_STATUS_BY_ACTION[action]
      if (status) {
        updateEventAttendee({ variables: { id: attendee.id, input: { status } } })
        return
      }

      if (action === 'move-to-top' || action === 'move-to-bottom') {
        handleWaitlistMoveAction(attendee, action).catch(() => undefined)
      }
    },
    [handleWaitlistMoveAction, updateEventAttendee]
  )

  const serverEntryIds = useMemo(() => pageData.entries.map((entry) => entry.id).join(','), [pageData.entries])

  useEffect(() => {
    if (!isDraggingRef.current) {
      setOrderedEntriesOverride(null)
    }
  }, [serverEntryIds])

  const handleWaitlistReorder = useCallback((newOrder: WaitlistAttendee[]) => {
    orderedEntriesRef.current = newOrder
    setOrderedEntriesOverride(newOrder)
  }, [])

  const handleWaitlistDragStart = useCallback((attendeeId: string) => {
    setPullToRefreshDisabled(true)
    isDraggingRef.current = true
    draggedAttendeeIdRef.current = attendeeId
    orderBeforeDragRef.current = orderedEntriesRef.current.map((entry) => entry.id)
    dragScrollSessionRef.current?.stop()
    dragScrollSessionRef.current = startPointerEdgeAutoScroll()
  }, [])

  const handleWaitlistDragEnd = useCallback(async () => {
    setPullToRefreshDisabled(false)
    dragScrollSessionRef.current?.stop()
    dragScrollSessionRef.current = null
    isDraggingRef.current = false
    const movedId = draggedAttendeeIdRef.current
    draggedAttendeeIdRef.current = null

    if (!movedId) {
      return
    }

    const beforeIds = orderBeforeDragRef.current
    const afterIds = orderedEntriesRef.current.map((entry) => entry.id)
    const newIndex = afterIds.indexOf(movedId)
    const oldIndex = beforeIds.indexOf(movedId)

    if (newIndex === -1 || oldIndex === -1 || newIndex === oldIndex) {
      return
    }

    const position = waitlistRankOnPage(newIndex, currentPage, pageData.perPage)

    try {
      await setWaitlistPosition({ variables: { id: movedId, position } })
    } catch {
      setOrderedEntriesOverride(null)
    }
  }, [currentPage, pageData.perPage, setWaitlistPosition])

  const canReorderWaitlist = Boolean(eventData?.event?.canEdit)
  const showReorderList = canReorderWaitlist && clientMounted

  const waitlistCards = displayEntries.map((attendee, index) => (
    <WaitlistAttendeeCard
      attendee={attendee}
      canEdit={canReorderWaitlist}
      displayPriority={waitlistRankOnPage(index, currentPage, pageData.perPage)}
      eventData={eventData}
      handleAction={handleAction}
      isReorderable={showReorderList}
      key={attendee.id}
      navigate={navigate}
      onDragEnd={showReorderList ? handleWaitlistDragEnd : undefined}
      onDragStart={showReorderList ? handleWaitlistDragStart : undefined}
      t={t}
    />
  ))

  if (error || eventError) {
    return <ErrorAlert error={error || eventError} />
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <EventAttendeesHeader activeTab={'waitlist'} event={eventData?.event} />

        <Form className="w-full" formMethods={formMethods}>
          <div className="flex w-full flex-col items-stretch gap-4">
            {/* Search and Filters */}
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Filter waitlist') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Filter waitlist by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            />

            {/* Results */}
            {displayEntries.length > 0 && (
              <div className="flex w-full flex-col gap-4">
                {showReorderList ? (
                  <Reorder.Group
                    as="div"
                    axis="y"
                    className="flex w-full flex-col gap-1"
                    onReorder={handleWaitlistReorder}
                    values={displayEntries}
                  >
                    {waitlistCards}
                  </Reorder.Group>
                ) : (
                  <div className="flex w-full flex-col gap-1">{waitlistCards}</div>
                )}
                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            )}

            {pageData.entries.length === 0 && (
              <Empty className="py-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserSearchIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No waitlist attendees found')}</EmptyTitle>
                  {resolvedSearch.name && (
                    <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
                  )}
                </EmptyHeader>
              </Empty>
            )}
          </div>
        </Form>
      </div>

      {/* Remove Attendee Modal */}
      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        onConfirm={() => {
          if (attendeeToRemove) {
            removeEventAttendee({
              variables: {
                id: attendeeToRemove.id,
                profileId: attendeeToRemove.profile.id,
              },
            })
          }
        }}
        onOpenChange={(e) => setRemoveModal(e.open)}
        open={removeModal}
        text={t('Are you sure you want to remove {{name}} from the event?', { name: attendeeToRemove?.profile?.name })}
        title={t('Remove Attendee')}
        variant="delete"
      />

      {selectedAttendee && (
        <EventAttendeeDialog
          attendee={selectedAttendee}
          event={eventData?.event}
          onOpenChange={(e) => setPaymentDialogOpen(e.open)}
          open={paymentDialogOpen}
        />
      )}
    </div>
  )
}

type WaitlistAttendeeCardProps = {
  attendee: WaitlistAttendee
  canEdit: boolean
  displayPriority: number
  eventData: EventAttendeesPageQuery | undefined
  handleAction: (attendee: WaitlistAttendee, action: string) => void
  isReorderable: boolean
  navigate: ReturnType<typeof useNavigate>
  onDragEnd?: () => void
  onDragStart?: (attendeeId: string) => void
  t: ReturnType<typeof useTranslation>['t']
}

function WaitlistAttendeeCard({
  attendee,
  canEdit,
  displayPriority,
  eventData,
  handleAction,
  isReorderable,
  navigate,
  onDragEnd,
  onDragStart,
  t,
}: WaitlistAttendeeCardProps) {
  const dragControls = useDragControls()
  const reorderItemRef = useRef<HTMLDivElement>(null)

  const handleReorderDragEnd = useCallback(() => {
    const element = reorderItemRef.current
    if (element) {
      element.style.removeProperty('width')
      element.style.removeProperty('height')
    }
    onDragEnd?.()
  }, [onDragEnd])

  const card = (
    <Card
      className={cn(
        'w-full overflow-hidden border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1',
        isReorderable ? 'cursor-default' : 'cursor-pointer'
      )}
      size="sm"
    >
      <CardContent className="px-3 py-1">
        <div className="flex w-full flex-col gap-0">
          <div className="flex w-full items-center justify-between gap-2">
            <Button
              aria-label={t('Drag to reorder waitlist')}
              className="shrink-0 cursor-grab touch-none rounded-full active:cursor-grabbing"
              disabled={!isReorderable}
              onPointerDown={(event) => dragControls.start(event)}
              size="icon"
              type="button"
              variant="ghost"
            >
              <GripVerticalIcon className="size-4 text-muted-foreground" />
            </Button>

            <div className="flex min-w-0 flex-1 items-center">
              <SFProfileBanner
                className="cursor-pointer"
                lowerTitle={
                  <div className="flex flex-col items-start gap-0">
                    <div className="flex flex-wrap items-center gap-1">
                      {canEdit && (
                        <Badge className="gap-1 bg-orange-500 font-semibold text-black" size="sm">
                          <span className="text-sm">#{displayPriority}</span>
                        </Badge>
                      )}

                      <Badge className="gap-1 bg-muted/80 text-foreground" size="sm" variant="secondary">
                        {attendee?.peopleQty > 1 ? (
                          <UsersIcon className="size-3.5 shrink-0" />
                        ) : (
                          <UserIcon className="size-3.5 shrink-0" />
                        )}
                        <span className="text-sm">{attendee?.peopleQty}</span>
                      </Badge>

                      {getEventRoleBadge({ id: attendee.id, role: attendee.role }, 'sm')}
                      {getEventAttendanceStatusBadge(attendee.status, 'sm')}

                      {canEdit && eventData?.event?.admission && attendee.paymentAmount > 0 && (
                        <Badge
                          className={cn(
                            'gap-1 text-foreground',
                            attendee.paymentStatus !== 'UNPAID'
                              ? attendee.paymentStatus !== 'PARTIAL'
                                ? 'bg-emerald-600'
                                : 'bg-amber-500'
                              : 'bg-destructive text-destructive-foreground'
                          )}
                          size="sm"
                          variant="secondary"
                        >
                          <CircleDollarSignIcon className="size-3.5 shrink-0" />
                          {t(attendee.paymentStatus)}
                          {attendee.paymentStatus === 'PAID' && ` $${attendee.paymentAmount.toFixed(2)}`}
                        </Badge>
                      )}
                    </div>

                    {canEdit && (
                      <p className="line-clamp-1 text-muted-foreground text-xs">
                        {t('Waitlisted: {{time}} - {{days}} days ago', {
                          time: dayjs(attendee.createdAt).format('hh:mm A'),
                          days: dayjs(dayjs()).diff(attendee.createdAt, 'days'),
                        })}
                      </p>
                    )}

                    {attendee.invitedBy && canEdit && (
                      <p className="line-clamp-1 text-muted-foreground text-xs">
                        {t('Invited by: {{name}}', { name: attendee.invitedBy.name })}
                      </p>
                    )}
                  </div>
                }
                onClick={() =>
                  navigate({
                    to: '/profile/$profileId/about',
                    params: { profileId: attendee.profile.id },
                  } as never)
                }
                profile={attendee.profile}
                size="xl"
              />
            </div>

            {canEdit && (
              <div className="flex shrink-0 items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button aria-label={t('Attendee actions')} className="rounded-full" size="icon" type="button">
                        <EllipsisIcon />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleAction(attendee, 'EDIT')}>
                      <PencilIcon />
                      {t('Edit')}
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={() => handleAction(attendee, 'move-to-top')}>
                      <ArrowUpIcon />
                      {t('Move to Top')}
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => handleAction(attendee, 'move-to-bottom')}>
                      <ArrowDownIcon />
                      {t('Move to Bottom')}
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      disabled={attendee.status === 'GOING'}
                      onClick={() => handleAction(attendee, 'going')}
                    >
                      <CheckIcon />
                      {t('Going')}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      disabled={attendee.status === 'MAYBE'}
                      onClick={() => handleAction(attendee, 'maybe')}
                    >
                      <ShieldQuestionIcon />
                      {t('Maybe')}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      disabled={attendee.status === 'NOTGOING'}
                      onClick={() => handleAction(attendee, 'notgoing')}
                    >
                      <XIcon />
                      {t('Not Going')}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      disabled={attendee.status === 'WAITLISTED'}
                      onClick={() => handleAction(attendee, 'waitlisted')}
                    >
                      <ClipboardListIcon />
                      {t('Waitlist')}
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={() => handleAction(attendee, 'remove')} variant="destructive">
                      <UserMinusIcon />
                      {t('Remove from Event')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {canEdit && attendee?.notes && (
            <div className="flex w-full flex-col items-start pt-1">
              <Separator className="w-full" />
              <div className="flex items-start justify-start gap-2 pt-1">
                <p className="whitespace-pre-line text-muted-foreground text-sm">{t('Notes:')}</p>
                <p className="whitespace-pre-line text-muted-foreground text-sm">{attendee?.notes}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )

  if (!isReorderable) {
    return card
  }

  return (
    <Reorder.Item
      as="div"
      className="relative w-full min-w-0 max-w-full list-none"
      dragControls={dragControls}
      dragListener={false}
      onDragEnd={handleReorderDragEnd}
      onDragStart={() => onDragStart?.(attendee.id)}
      ref={reorderItemRef}
      style={{ width: '100%' }}
      value={attendee}
      whileDrag={{
        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
        zIndex: 10,
      }}
    >
      {card}
    </Reorder.Item>
  )
}
